// components/accounts/charts.tsx — the month charts on the Accounts and Analytics tabs (Task N). Recharts, the
// project's validated palette in fixed order, one y-axis per chart, a tooltip on every bar/point, a legend for 2+ series.
import type { ReactNode } from 'react';
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { AXIS_COLOR, GRID_COLOR, TEXT_MUTED, STATUS_VAR, seriesColor, formatMoney, formatMoneyCompact } from '../../lib/viz';

type Row = Record<string, number | string | null>;
export interface Series { key: string; label: string; colour?: string }

const legend = (series: Series[]) => series.length > 1 && (
  <div className="mt-2 flex flex-wrap gap-3 text-[11.5px] text-ink-500">
    {series.map((s, i) => <span key={s.key} className="inline-flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: s.colour ?? seriesColor(i) }} />{s.label}</span>)}
  </div>
);

/** Bars per month (grouped, or stacked with `stacked`), money on the axis. */
export function MonthBars({ data, series, currency, stacked, height = 200, xKey = 'label' }: { data: Row[]; series: Series[]; currency: string; stacked?: boolean; height?: number; xKey?: string }) {
  return (
    <div>
      <div style={{ height }} className="w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 6, right: 4, bottom: 0, left: 0 }} barGap={2} barCategoryGap="22%">
            <CartesianGrid vertical={false} stroke={GRID_COLOR} />
            <XAxis dataKey={xKey} tick={{ fontSize: 10.5, fill: TEXT_MUTED }} axisLine={{ stroke: AXIS_COLOR }} tickLine={false} interval="preserveStartEnd" />
            <YAxis width={48} tick={{ fontSize: 10.5, fill: TEXT_MUTED }} axisLine={false} tickLine={false} tickFormatter={(v) => formatMoneyCompact(Number(v), currency)} />
            <Tooltip formatter={(v, name) => [formatMoney(Number(v ?? 0), currency), series.find((s) => s.key === String(name))?.label ?? String(name)]} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            {series.map((s, i) => <Bar key={s.key} dataKey={s.key} stackId={stacked ? 'a' : undefined} fill={s.colour ?? seriesColor(i)} radius={stacked && i < series.length - 1 ? 0 : [4, 4, 0, 0]} stroke="var(--color-white)" strokeWidth={stacked ? 1 : 0} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>
      {legend(series)}
    </div>
  );
}

/** A line per month for a rate or a count (`unit` '%' or ''). */
export function MonthLine({ data, series, unit = '%', height = 180, domain }: { data: Row[]; series: Series[]; unit?: string; height?: number; domain?: [number, number] }) {
  return (
    <div>
      <div style={{ height }} className="w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID_COLOR} />
            <XAxis dataKey="label" tick={{ fontSize: 10.5, fill: TEXT_MUTED }} axisLine={{ stroke: AXIS_COLOR }} tickLine={false} interval="preserveStartEnd" />
            <YAxis width={40} domain={domain ?? ['auto', 'auto']} tick={{ fontSize: 10.5, fill: TEXT_MUTED }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}${unit}`} />
            <Tooltip formatter={(v, name) => [`${Number(v ?? 0).toLocaleString('en-GB')}${unit}`, series.find((s) => s.key === String(name))?.label ?? String(name)]} />
            {series.map((s, i) => <Line key={s.key} type="monotone" dataKey={s.key} stroke={s.colour ?? seriesColor(i)} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />)}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {legend(series)}
    </div>
  );
}

/** A labelled horizontal bar: used for budget vs actual and "where the money goes". */
export function MeterRow({ label, value, max, tone = 'neutral', right, sub }: { label: ReactNode; value: number; max: number; tone?: 'good' | 'warning' | 'critical' | 'neutral'; right: ReactNode; sub?: ReactNode }) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  const colour = { good: STATUS_VAR.good, warning: STATUS_VAR.warning, critical: STATUS_VAR.critical, neutral: seriesColor(0) }[tone];
  return (
    <div className="py-2">
      <div className="flex items-baseline justify-between gap-3 text-[12.5px]"><span className="min-w-0 truncate text-ink-800">{label}</span><span className="shrink-0 tabular-nums text-ink-700">{right}</span></div>
      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-ink-100" title={`${Math.round(pct)}%`}><div className="h-full rounded-full" style={{ width: `${pct}%`, background: colour }} /></div>
      {sub && <div className="mt-1 text-[11px] text-ink-400">{sub}</div>}
    </div>
  );
}
